import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface AdminInvitation {
  id: string
  invitedEmail: string
  grantSuperadmin: boolean
  status: 'pending' | 'accepted' | 'revoked'
  expiresAt: string
  createdAt: string
}

// Pending/historical admin invitations, readable via admin_invitations' "superadmin
// read" RLS policy (migration 068) — a plain admin (non-superadmin) sees nothing here,
// matching is_superadmin()'s gate. Create/revoke go through invite-admin (Edge
// Function, wraps create_admin_invitation) and revoke_admin_invitation (RPC) directly,
// mirroring useAdminDirectory's existing split between a direct RPC call and an Edge
// Function call depending on whether the write needs service-role.
export function useAdminInvitations() {
  const [invitations, setInvitations] = useState<AdminInvitation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [working, setWorking] = useState(false)

  const fetchInvitations = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: fetchErr } = await supabase
      .from('admin_invitations')
      .select('id, invited_email, grant_superadmin, status, expires_at, created_at')
      .order('created_at', { ascending: false })
    if (fetchErr) { setError('Could not load invitations.'); setLoading(false); return }
    setInvitations((data ?? []).map((r: any) => ({
      id: r.id, invitedEmail: r.invited_email, grantSuperadmin: r.grant_superadmin,
      status: r.status, expiresAt: r.expires_at, createdAt: r.created_at,
    })))
    setLoading(false)
  }, [])

  useEffect(() => { fetchInvitations() }, [fetchInvitations])

  const inviteAdmin = useCallback(async (email: string, grantSuperadmin: boolean) => {
    setWorking(true)
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('invite-admin', {
        body: { email, grantSuperadmin },
      })
      if (fnErr || data?.ok === false) {
        return { ok: false, error: data?.code ?? 'Could not send invitation.' }
      }
      await fetchInvitations()
      return { ok: true, emailed: data?.emailed as boolean, token: data?.token as string | undefined }
    } finally {
      setWorking(false)
    }
  }, [fetchInvitations])

  const revokeInvitation = useCallback(async (invitationId: string) => {
    setWorking(true)
    try {
      const { data, error: rpcErr } = await supabase.rpc('revoke_admin_invitation', { p_invitation_id: invitationId })
      const result = data as { ok: boolean; code?: string } | null
      if (rpcErr || !result?.ok) return { ok: false, error: result?.code ?? 'Could not revoke invitation.' }
      await fetchInvitations()
      return { ok: true }
    } finally {
      setWorking(false)
    }
  }, [fetchInvitations])

  return { invitations, loading, error, working, refresh: fetchInvitations, inviteAdmin, revokeInvitation }
}
